import { useState, useMemo, useRef, useEffect } from 'react';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps';
import { geoCentroid } from 'd3-geo';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

// ISO 3166-1 alpha-2 → alpha-3 map for the most common countries from click data
const A2_TO_A3 = {
  US: 'USA', GB: 'GBR', DE: 'DEU', FR: 'FRA', CA: 'CAN', AU: 'AUS', NL: 'NLD',
  IN: 'IND', BR: 'BRA', JP: 'JPN', CN: 'CHN', SG: 'SGP', MX: 'MEX', ES: 'ESP',
  IT: 'ITA', RU: 'RUS', KR: 'KOR', SE: 'SWE', NO: 'NOR', DK: 'DNK', FI: 'FIN',
  PL: 'POL', CH: 'CHE', AT: 'AUT', BE: 'BEL', PT: 'PRT', CZ: 'CZE', HU: 'HUN',
  RO: 'ROU', GR: 'GRC', TR: 'TUR', ZA: 'ZAF', NG: 'NGA', EG: 'EGY', KE: 'KEN',
  AR: 'ARG', CL: 'CHL', CO: 'COL', PK: 'PAK', BD: 'BGD', ID: 'IDN', TH: 'THA',
  VN: 'VNM', PH: 'PHL', MY: 'MYS', TW: 'TWN', HK: 'HKG', IL: 'ISR', AE: 'ARE',
  SA: 'SAU', IR: 'IRN', UA: 'UKR', NZ: 'NZL', IE: 'IRL', CY: 'CYP', HR: 'HRV',
  SK: 'SVK', SI: 'SVN', BG: 'BGR', LT: 'LTU', LV: 'LVA', EE: 'EST', RS: 'SRB',
  BY: 'BLR', MD: 'MDA', GE: 'GEO', AM: 'ARM', AZ: 'AZE', KZ: 'KAZ', UZ: 'UZB',
  PE: 'PER', EC: 'ECU', VE: 'VEN', BO: 'BOL', PY: 'PRY', UY: 'URY', CR: 'CRI',
  GT: 'GTM', CU: 'CUB', DO: 'DOM', PR: 'PRI', PA: 'PAN', HN: 'HND', SV: 'SLV',
  NI: 'NIC', MA: 'MAR', DZ: 'DZA', TN: 'TUN', LY: 'LBY', SD: 'SDN', ET: 'ETH',
  GH: 'GHA', TZ: 'TZA', UG: 'UGA', SN: 'SEN', CI: 'CIV', CM: 'CMR', MZ: 'MOZ',
  MG: 'MDG', AO: 'AGO', ZM: 'ZMB', ZW: 'ZWE', BW: 'BWA', NA: 'NAM', MU: 'MUS',
  LK: 'LKA', MM: 'MMR', KH: 'KHM', LA: 'LAO', NP: 'NPL', AF: 'AFG', IQ: 'IRQ',
  SY: 'SYR', JO: 'JOR', LB: 'LBN', KW: 'KWT', QA: 'QAT', BH: 'BHR', OM: 'OMN',
  YE: 'YEM', LU: 'LUX', MT: 'MLT', IS: 'ISL', LI: 'LIE', MC: 'MCO', SM: 'SMR',
  MK: 'MKD', AL: 'ALB', BA: 'BIH', ME: 'MNE', XK: 'XKX', MN: 'MNG',
};

const NAME_TO_A3 = {
  'United States': 'USA', 'United Kingdom': 'GBR', 'Germany': 'DEU', 'France': 'FRA',
  'Canada': 'CAN', 'Australia': 'AUS', 'Netherlands': 'NLD', 'India': 'IND',
  'Brazil': 'BRA', 'Japan': 'JPN', 'China': 'CHN', 'Singapore': 'SGP',
  'Mexico': 'MEX', 'Spain': 'ESP', 'Italy': 'ITA', 'Russia': 'RUS',
  'South Korea': 'KOR', 'Sweden': 'SWE', 'Norway': 'NOR', 'Denmark': 'DNK',
  'Finland': 'FIN', 'Poland': 'POL', 'Switzerland': 'CHE', 'Austria': 'AUT',
  'Belgium': 'BEL', 'Portugal': 'PRT', 'Czech Republic': 'CZE', 'Hungary': 'HUN',
  'Romania': 'ROU', 'Greece': 'GRC', 'Turkey': 'TUR', 'South Africa': 'ZAF',
  'Nigeria': 'NGA', 'Egypt': 'EGY', 'Kenya': 'KEN', 'Argentina': 'ARG',
  'Chile': 'CHL', 'Colombia': 'COL', 'Pakistan': 'PAK', 'Bangladesh': 'BGD',
  'Indonesia': 'IDN', 'Thailand': 'THA', 'Vietnam': 'VNM', 'Philippines': 'PHL',
  'Malaysia': 'MYS', 'Taiwan': 'TWN', 'Hong Kong': 'HKG', 'Israel': 'ISR',
  'United Arab Emirates': 'ARE', 'Saudi Arabia': 'SAU', 'Iran': 'IRN',
  'Ukraine': 'UKR', 'New Zealand': 'NZL', 'Ireland': 'IRL',
};

function getA2(country) {
  const upper = country?.toUpperCase().trim() || '';
  if (/^[A-Z]{2}$/.test(upper)) return upper;
  const a3 = NAME_TO_A3[country] || A2_TO_A3[upper];
  if (!a3) return null;
  const a2Entry = Object.entries(A2_TO_A3).find(([, v]) => v === a3);
  return a2Entry ? a2Entry[0] : null;
}

function getFlag(country) {
  const a2 = getA2(country);
  if (!a2) return '🌍';
  return String.fromCodePoint(...[...a2].map((c) => 0x1F1E6 - 65 + c.charCodeAt(0)));
}

function resolveA3(country) {
  if (!country) return null;
  const upper = country.toUpperCase().trim();
  if (A2_TO_A3[upper]) return A2_TO_A3[upper];
  if (NAME_TO_A3[country]) return NAME_TO_A3[country];
  if (/^[A-Z]{3}$/.test(upper)) return upper;
  return null;
}

// SVG viewBox dimensions tuned so geoEqualEarth fills with no empty bands.
const MAP_VB_WIDTH = 980;
const MAP_VB_HEIGHT = 460;
const DEFAULT_CENTER = [10, 12];
const DEFAULT_ZOOM = 1;
const BRAND = '#4648d4';

// Container width threshold above which we render the side-by-side layout.
const WIDE_BREAKPOINT = 640;

export default function WorldClickMap({ data = [], height = 320 }) {
  const rootRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [hoveredA3, setHoveredA3] = useState(null);
  const [position, setPosition] = useState({ coordinates: DEFAULT_CENTER, zoom: DEFAULT_ZOOM });
  const [containerWidth, setContainerWidth] = useState(0);

  // Measure the component's actual width so the layout adapts to its
  // immediate container (works inside narrow grid cells too, not just at
  // viewport breakpoints).
  useEffect(() => {
    if (!rootRef.current) return;
    const el = rootRef.current;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    ro.observe(el);
    setContainerWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  const isWide = containerWidth >= WIDE_BREAKPOINT;

  const countryMap = useMemo(() => {
    const map = {};
    for (const row of data) {
      const a3 = resolveA3(row.name);
      if (a3) map[a3] = { name: row.name, value: row.value };
    }
    return map;
  }, [data]);

  const total = useMemo(() => data.reduce((s, d) => s + d.value, 0), [data]);
  const top = data.slice(0, isWide ? 8 : 5);

  const handleMouseMove = (e, info) => {
    if (!info || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setTooltip({
      name: info.name,
      value: info.value,
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const handleZoom = (delta) => {
    setPosition((p) => ({ ...p, zoom: Math.min(6, Math.max(1, p.zoom + delta)) }));
  };

  const handleReset = () => {
    setPosition({ coordinates: DEFAULT_CENTER, zoom: DEFAULT_ZOOM });
    setSelectedCountry(null);
  };

  return (
    <div
      ref={rootRef}
      className={`w-full ${isWide ? 'flex flex-row gap-6 items-stretch' : 'flex flex-col gap-5'}`}
    >
      {/* Map */}
      <div
        ref={containerRef}
        className="relative rounded-2xl overflow-hidden border border-border/40 bg-gradient-to-br from-white via-[#FAFAFC] to-[#F4F4FA]"
        style={{
          aspectRatio: `${MAP_VB_WIDTH} / ${MAP_VB_HEIGHT}`,
          flex: isWide ? '1 1 0' : 'none',
          width: isWide ? undefined : '100%',
          minHeight: isWide ? Math.max(height, 220) : 200,
        }}
      >
        <ComposableMap
          projection="geoEqualEarth"
          width={MAP_VB_WIDTH}
          height={MAP_VB_HEIGHT}
          projectionConfig={{ scale: 175 }}
          style={{ width: '100%', height: '100%', display: 'block' }}
        >
          <defs>
            {/* Continuous dot pattern that tiles across the entire SVG, so
                dots align across all land regardless of country boundaries. */}
            <pattern id="land-dots" width="6" height="6" patternUnits="userSpaceOnUse">
              <circle cx="3" cy="3" r="1" fill="#D6D7E4" />
            </pattern>
            <pattern id="land-dots-active" width="6" height="6" patternUnits="userSpaceOnUse">
              <circle cx="3" cy="3" r="1.15" fill="#A9ABE8" />
            </pattern>
            <pattern id="land-dots-hover" width="6" height="6" patternUnits="userSpaceOnUse">
              <circle cx="3" cy="3" r="1.25" fill={BRAND} />
            </pattern>
          </defs>

          <ZoomableGroup
            zoom={position.zoom}
            center={position.coordinates}
            minZoom={1}
            maxZoom={6}
            onMoveEnd={(p) => setPosition(p)}
          >
            <Geographies geography={GEO_URL}>
              {({ geographies }) => {
                const markers = geographies
                  .map((geo) => {
                    const a3 = geo.properties.a3 || geo.properties.ISO_A3 || geo.id;
                    const info = countryMap[a3];
                    if (!info) return null;
                    return { a3, info, centroid: geoCentroid(geo) };
                  })
                  .filter(Boolean)
                  .sort((a, b) => b.info.value - a.info.value);

                const topA3 = markers[0]?.a3;

                return (
                  <>
                    {/* Countries — rendered as dotted shapes */}
                    {geographies.map((geo) => {
                      const a3 = geo.properties.a3 || geo.properties.ISO_A3 || geo.id;
                      const info = countryMap[a3];
                      const isHovered = hoveredA3 === a3;
                      const isSelected = selectedCountry === a3;
                      let fill = 'url(#land-dots)';
                      if (isHovered || isSelected) fill = 'url(#land-dots-hover)';
                      else if (info) fill = 'url(#land-dots-active)';

                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          fill={fill}
                          stroke="none"
                          style={{
                            default: { outline: 'none', cursor: info ? 'pointer' : 'default' },
                            hover: { outline: 'none', cursor: info ? 'pointer' : 'default' },
                            pressed: { outline: 'none' },
                          }}
                          onMouseEnter={(e) => {
                            if (!info) return;
                            setHoveredA3(a3);
                            handleMouseMove(e, info);
                          }}
                          onMouseMove={(e) => info && handleMouseMove(e, info)}
                          onMouseLeave={() => {
                            setHoveredA3(null);
                            setTooltip(null);
                          }}
                          onClick={() => info && setSelectedCountry(isSelected ? null : a3)}
                        />
                      );
                    })}

                    {/* Flag markers for countries with data */}
                    {markers.map(({ a3, info, centroid }) => {
                      const isTop = a3 === topA3;
                      const isSelected = selectedCountry === a3;
                      const flag = getFlag(info.name);
                      const showLabel = isTop || isSelected || hoveredA3 === a3;
                      return (
                        <Marker
                          key={a3}
                          coordinates={centroid}
                          onMouseEnter={(e) => {
                            setHoveredA3(a3);
                            handleMouseMove(e, info);
                          }}
                          onMouseMove={(e) => handleMouseMove(e, info)}
                          onMouseLeave={() => {
                            setHoveredA3(null);
                            setTooltip(null);
                          }}
                          onClick={() => setSelectedCountry(isSelected ? null : a3)}
                          style={{ cursor: 'pointer' }}
                        >
                          {/* Pulsing ring under the top country */}
                          {isTop && (
                            <>
                              <circle r={18} fill={BRAND} fillOpacity={0.14}>
                                <animate
                                  attributeName="r"
                                  values="14;26;14"
                                  dur="2.4s"
                                  repeatCount="indefinite"
                                />
                                <animate
                                  attributeName="fill-opacity"
                                  values="0.28;0.04;0.28"
                                  dur="2.4s"
                                  repeatCount="indefinite"
                                />
                              </circle>
                              <circle r={14} fill={BRAND} fillOpacity={0.22} />
                            </>
                          )}
                          {/* Flag pin */}
                          <circle
                            r={9}
                            fill="#FFFFFF"
                            stroke={isTop || isSelected ? BRAND : '#D6D7E4'}
                            strokeWidth={isTop || isSelected ? 2 : 1.5}
                            style={{ filter: 'drop-shadow(0 2px 4px rgba(15,15,40,0.18))' }}
                          />
                          <text
                            textAnchor="middle"
                            dominantBaseline="central"
                            style={{
                              fontSize: 11,
                              pointerEvents: 'none',
                              userSelect: 'none',
                            }}
                          >
                            {flag}
                          </text>
                          {/* Floating value label */}
                          {showLabel && (
                            <g transform="translate(0, -22)" style={{ pointerEvents: 'none' }}>
                              <rect
                                x={-38}
                                y={-13}
                                width={76}
                                height={20}
                                rx={5}
                                fill="#FFFFFF"
                                stroke="#E5E5EE"
                                strokeWidth={0.8}
                                style={{ filter: 'drop-shadow(0 2px 6px rgba(15,15,40,0.10))' }}
                              />
                              <text
                                textAnchor="middle"
                                y={-2}
                                style={{
                                  fontSize: 8.5,
                                  fontWeight: 600,
                                  fill: '#6B6E80',
                                  letterSpacing: 0.2,
                                }}
                              >
                                {info.name.toUpperCase().slice(0, 14)}
                              </text>
                              <text
                                textAnchor="middle"
                                y={4.5}
                                style={{ fontSize: 9, fontWeight: 700, fill: '#1A1B2E' }}
                              >
                                {info.value.toLocaleString()}
                              </text>
                            </g>
                          )}
                        </Marker>
                      );
                    })}
                  </>
                );
              }}
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>

        {/* Floating tooltip */}
        {tooltip && (
          <div
            className="absolute z-30 pointer-events-none bg-foreground text-background text-xs font-semibold px-2.5 py-1.5 rounded-lg shadow-lg whitespace-nowrap"
            style={{
              left: Math.min(tooltip.x + 14, (containerRef.current?.clientWidth || 9999) - 170),
              top: Math.max(tooltip.y - 38, 4),
            }}
          >
            <span className="mr-1.5">{getFlag(tooltip.name)}</span>
            {tooltip.name} · {tooltip.value.toLocaleString()} clicks
          </div>
        )}

        {/* Zoom controls */}
        <div className="absolute top-3 right-3 flex flex-col bg-background/85 backdrop-blur rounded-lg shadow-sm border border-border/40 overflow-hidden">
          <button
            type="button"
            onClick={() => handleZoom(0.6)}
            className="w-7 h-7 grid place-items-center text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors text-base leading-none"
            aria-label="Zoom in"
          >
            +
          </button>
          <div className="h-px bg-border/40" />
          <button
            type="button"
            onClick={() => handleZoom(-0.6)}
            className="w-7 h-7 grid place-items-center text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors text-base leading-none"
            aria-label="Zoom out"
          >
            −
          </button>
          <div className="h-px bg-border/40" />
          <button
            type="button"
            onClick={handleReset}
            className="w-7 h-7 grid place-items-center text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors text-[11px] font-semibold"
            aria-label="Reset view"
          >
            ⤾
          </button>
        </div>
      </div>

      {/* Side panel */}
      <div
        className="flex flex-col"
        style={{
          flex: isWide ? '0 0 280px' : 'none',
          width: isWide ? 280 : '100%',
        }}
      >
        {isWide && (
          <>
            <p className="font-display text-3xl font-bold tabular-nums text-foreground tracking-tight leading-none">
              {total.toLocaleString()}
            </p>
            <div className="flex items-center gap-2 mt-1.5 mb-5">
              <p className="text-xs text-muted-foreground">Total clicks</p>
              {top.length > 0 && (
                <span className="text-[11px] font-semibold text-emerald-600 inline-flex items-center gap-0.5">
                  <span className="text-sm leading-none">↑</span>
                  {Math.round((top[0].value / Math.max(total, 1)) * 100)}% top
                </span>
              )}
            </div>
          </>
        )}

        {!isWide && (
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-3">
            Top countries
          </p>
        )}

        {top.length === 0 && (
          <p className="text-xs text-muted-foreground">No data</p>
        )}

        <div className={isWide ? 'space-y-3' : 'space-y-2'}>
          {top.map((row, i) => {
            const pct = total > 0 ? Math.round((row.value / total) * 100) : 0;
            const a3 = resolveA3(row.name);
            const isSelected = selectedCountry === a3;
            return (
              <button
                key={row.name}
                onClick={() => setSelectedCountry(isSelected ? null : a3)}
                className="w-full text-left flex items-center gap-3 group transition-colors"
              >
                <span
                  className={`shrink-0 grid place-items-center rounded-full bg-white border transition-all ${
                    isWide ? 'w-9 h-9 text-base' : 'w-8 h-8 text-sm'
                  } ${
                    isSelected
                      ? 'border-primary shadow-[0_0_0_3px_rgba(70,72,212,0.15)]'
                      : 'border-border/60 group-hover:border-primary/40'
                  }`}
                >
                  {getFlag(row.name)}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <span className="text-sm font-medium text-foreground truncate">{row.name}</span>
                    <span className="text-xs font-semibold text-muted-foreground tabular-nums shrink-0">
                      {pct}%
                    </span>
                  </div>
                  <div className="h-2 bg-muted/70 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700 ease-out"
                      style={{
                        width: `${pct}%`,
                        background: i === 0
                          ? `linear-gradient(90deg, ${BRAND} 0%, #6366f1 100%)`
                          : `linear-gradient(90deg, hsl(${245 + i * 10}, 65%, ${58 + i * 2}%) 0%, hsl(${250 + i * 10}, 70%, ${64 + i * 2}%) 100%)`,
                      }}
                    />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
